import type { FC, ReactNode } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { Button } from 'src/components/buttons-and-actions/button';

type Props = {
  lines: number;
  children: ReactNode;
  alwaysExpanded?: boolean;
  id?: string;
  textTestId?: string;
  toggleTestId?: string;
};

export const ExpandableText: FC<Props> = ({
  children,
  lines,
  alwaysExpanded = false,
  id,
  textTestId,
  toggleTestId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const collapsed = !expanded && !alwaysExpanded;

  useLayoutEffect(() => {
    const container = containerRef.current;
    // only measure while collapsed: once expanded, the clamp is removed so
    // scrollHeight reflects the full content height, which would otherwise
    // make isOverflowing (and the toggle label) flicker while expanded
    if (!container || alwaysExpanded || !collapsed) {
      return;
    }

    const checkOverflow = () => {
      setIsOverflowing(container.scrollHeight > container.clientHeight);
    };
    checkOverflow();

    // re-check whenever the container is resized
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [children, alwaysExpanded, lines, collapsed]);

  return (
    <div className="flex flex-col gap-xs">
      <div
        ref={containerRef}
        id={id}
        data-testid={textTestId}
        // esoteric CSS properties - no need to migrate to Tailwind classes
        // oxlint-disable-next-line prisma/tailwind-no-inline-styles
        style={
          collapsed
            ? {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: lines,
                overflow: 'hidden',
              }
            : undefined
        }
      >
        {children}
      </div>

      {isOverflowing && !alwaysExpanded && (
        <div className="flex justify-end">
          <Button
            mode="link-underlined"
            data-testid={toggleTestId}
            aria-expanded={expanded}
            aria-controls={id}
            onClick={() => setExpanded((previous) => !previous)}
          >
            {expanded ? 'Show Less' : 'Show More'}
          </Button>
        </div>
      )}
    </div>
  );
};
